import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { withRouter } from 'react-router-dom';
import { useIncompleteExamsCount, useIncompleteExamsData, invalidateAllWidgetQueries } from '../../../hooks/useWidgetData';
import { toast } from '@rickylandino/react-messages';
import { CheckCircle } from 'lucide-react';
import { Table, Modal } from 'antd';
import Globals from '../../../config/globals';
import PatientExamPane from '../../Exams/PatientExamPane';
import { calculateAge } from '../../Helpers/PatientHelper';
import { getPatientExamResults } from '../../../common/services/ExamService';
import { getAllPatientExamInfoByPatient, getPatientInformationByIDNew } from '../../../common/services/ApiService';

/**
 * IncompleteExamsWidget component
 * Shows all exams missing required information (exam type, provider, or insurance)
 * @param {Object} props
 * @param {boolean} [props.isCollapsed=false] - Whether the widget is collapsed
 * @param {boolean} [props.isExpanded=false] - Whether the widget is in fullscreen mode
 * @param {Function} [props.setIsLoading] - Callback to set loading state for parent
 * @param {Function} [props.setSharedState] - Callback to update shared state (including refetch)
 */
function IncompleteExamsWidget({ isCollapsed = false, isExpanded = false, setIsLoading, setSharedState, queryEnabled = true, history }) {
    // Get query client for cache invalidation
    const queryClient = useQueryClient();
    
    // Pagination state
    const [currentPage, setCurrentPage] = useState(1);
    const [pageSize, setPageSize] = useState(50);
    
    // Exam pane state
    const [showAddExamPane, setShowAddExamPane] = useState(false);
    const [selectedPatient, setSelectedPatient] = useState(null);
    const [selectedPatientExam, setSelectedPatientExam] = useState(null);
    const [examResults, setExamResults] = useState(null);
    const [sharedFollowUpIDs, setSharedFollowUpIDs] = useState(null);
    const [facility, setFacility] = useState(null);

    // Duplicate exam check state
    const [showDuplicateModal, setShowDuplicateModal] = useState(false);
    const [pendingExamData, setPendingExamData] = useState(null);

    // Determine when to load full data: either expanded in card OR in fullscreen mode
    const shouldLoadFullData = !isCollapsed || isExpanded;

    // Fetch count when collapsed AND not expanded
    const { data: countData, isLoading: isLoadingCount } = useIncompleteExamsCount((isCollapsed && !isExpanded) && queryEnabled);

    // Fetch full data when expanded in card or fullscreen
    const { 
        data, 
        isLoading, 
        isFetching,
        isError, 
        error,
        refetch
    } = useIncompleteExamsData(currentPage, pageSize, shouldLoadFullData);

    const results = data?.data || [];
    const totalCount = data?.totalCount || 0;
    const count = shouldLoadFullData ? totalCount : (countData?.count || 0);

    // Notify parent of loading state for fullscreen overlay
    useEffect(() => {
        if (setIsLoading && isExpanded) {
            setIsLoading(isLoading || isFetching);
        }
    }, [isLoading, isFetching, isExpanded, setIsLoading]);

    // Show error toast when full data fails to load
    useEffect(() => {
        if (shouldLoadFullData && isError) {
            console.error('Failed to load incomplete exams:', error);
            toast.error(`Failed to load incomplete exams: ${error?.message || 'Unknown error'}`);
        }
    }, [shouldLoadFullData, isError, error]);

    // Set refetch function in shared state when it becomes available
    useEffect(() => {
        if (setSharedState && refetch) {
            setSharedState(prev => ({ ...prev, refetch }));
        }
    }, [setSharedState, refetch]);

    /**
     * Format MRN with spaces every 10 characters - memoized
     */
    const formatMRN = useCallback((mrn) => {
        if (!mrn) return '';
        return mrn.replace(/(.{10})/g, '$1 ').trim();
    }, []);

    /**
     * Format date to short format - memoized
     */
    const formatDate = useCallback((dateString) => {
        if (!dateString) return '';
        const date = new Date(dateString);
        return date.toLocaleDateString('en-US', { 
            month: 'numeric', 
            day: 'numeric', 
            year: 'numeric' 
        });
    }, []);

    /**
     * Sorted results - most recent exams first
     */
    const sortedResults = useMemo(() => {
        if (!results || results.length === 0) return [];
        
        return [...results].sort((a, b) => {
            const aDate = new Date(a.examDateTime).getTime();
            const bDate = new Date(b.examDateTime).getTime();
            return bDate - aDate; // Most recent first
        });
    }, [results]);

    /**
     * Open the exam pane with already-fetched data
     */
    const openExamPane = useCallback((row, examData, patient) => {
        setSelectedPatient(patient);
        setSelectedPatientExam(examData.examInfo);
        setExamResults(examData.resultInfo);
        setSharedFollowUpIDs(examData.examIDs);
        setFacility({ facilityID: row.facilityID, facilityName: row.facilityName });
        setShowAddExamPane(true);
    }, []);

    /**
     * Handle performing an exam - checks for same-day duplicate category exams first
     */
    const handlePerformExam = useCallback(async (row) => {
        try {
            const userInfo = JSON.parse(window.sessionStorage.getItem("userInfo"));

            if (!userInfo?.userId) {
                toast.error('User information not found. Please log in again.');
                return;
            }

            if (!row.patientExamID || !row.facilityID) {
                toast.error('Missing required exam information');
                console.error('Missing fields:', { row });
                return;
            }

            const postdata = {
                uniqueID: row.patientExamID,
                facilityID: row.facilityID,
                name: row.examClass,
                userID: userInfo.userId
            };

            const [examData, patientExams] = await Promise.all([
                getPatientExamResults(postdata),
                getAllPatientExamInfoByPatient(row.patientID)
            ]);

            if (!examData) throw new Error('No data returned from server');

            const patient = {
                dob: row.patientDob || row.dob,
                patientName: row.patientName,
                mrn: row.mrn
            };

            // Check for duplicate exams: same category, same day, different exam
            if (patientExams?.length > 0) {
                const currentExam = patientExams.find(e => e.patientExamModel?.patientExamID === row.patientExamID);
                if (currentExam) {
                    const currentCategoryID = currentExam.examTypeModel?.categoryID;
                    const currentDateStr = new Date(row.examDateTime).toISOString().split('T')[0];

                    const hasDuplicate = patientExams.some(e =>
                        e.patientExamModel?.patientExamID !== row.patientExamID &&
                        e.examTypeModel?.categoryID === currentCategoryID &&
                        new Date(e.patientExamModel?.examDateTime).toISOString().split('T')[0] === currentDateStr
                    );

                    if (hasDuplicate) {
                        setPendingExamData({ row, examData, patient });
                        setShowDuplicateModal(true);
                        return;
                    }
                }
            }

            openExamPane(row, examData, patient);

        } catch (error) {
            console.error('Error loading exam:', error);
            toast.error(`Failed to load exam: ${error?.message || 'Unknown error'}`);
        }
    }, [openExamPane]);

    /**
     * Navigate to the patient's chart to review and remove duplicate exams
     */
    const handleDuplicateReview = useCallback(async () => {
        if (!pendingExamData?.row?.patientID) return;
        setShowDuplicateModal(false);
        try {
            const data = await getPatientInformationByIDNew(pendingExamData.row.patientID);
            history.push({
                pathname: '/patient-dashboard',
                state: { selectedObject: data.patientInformation, examInfo: data.examInfo, patientEthnicity: data.patientEthnicity }
            });
        } catch (error) {
            toast.error('Failed to navigate to patient chart.');
        }
        setPendingExamData(null);
    }, [pendingExamData, history]);

    /**
     * Dismiss the duplicate warning and open the exam pane anyway
     */
    const handleDuplicateIgnore = useCallback(() => {
        setShowDuplicateModal(false);
        if (pendingExamData) {
            const { row, examData, patient } = pendingExamData;
            openExamPane(row, examData, patient);
        }
        setPendingExamData(null);
    }, [pendingExamData, openExamPane]);

    /**
     * Update exam info after changes
     */
    const updateExamInfo = useCallback(() => {
        toast.destroy('loading');
        toast.destroy("updating");

        setShowAddExamPane(false);

        // Invalidate all widget queries since exam changes can affect multiple widgets
        invalidateAllWidgetQueries(queryClient);

        toast.success('Widget data refreshed');
    }, [queryClient]);

    /**
     * Get new exam info and refresh widget
     */
    const getNewExamInfo = useCallback(() => {
        toast.destroy('loading');
        toast.destroy("updating");

        setShowAddExamPane(false);

        // Invalidate all widget queries since exam changes can affect multiple widgets
        invalidateAllWidgetQueries(queryClient);
        
        toast.success('Widget data refreshed');
    }, [queryClient]);

    /**
     * Table columns configuration - memoized to prevent recreation
     */
    const columns = useMemo(() => [
        {
            title: 'Patient Name',
            dataIndex: 'patientName',
            key: 'patientName',
            width: 200,
        },
        {
            title: 'MRN',
            dataIndex: 'mrn',
            key: 'mrn',
            render: (mrn) => <span style={{ fontFamily: 'monospace', fontSize: '0.875rem' }}>{formatMRN(mrn)}</span>,
            width: 150,
        },
        {
            title: 'Facility',
            dataIndex: 'facilityName',
            key: 'facilityName',
            width: 180,
        },
        {
            title: 'Exam Date',
            dataIndex: 'examDateTime',
            key: 'examDateTime',
            render: (examDateTime) => formatDate(examDateTime),
            width: 140,
        },
        {
            title: 'Exam Type',
            dataIndex: 'examType',
            key: 'examType',
            render: (text, record) => 
                record.missingExamType ? 
                    <span style={{ color: '#f59e0b', fontStyle: 'italic' }}>Missing</span> : 
                    text,
            width: 150,
        },
        {
            title: 'Provider',
            dataIndex: 'providerName',
            key: 'providerName',
            render: (text, record) => 
                record.missingProvider ? 
                    <span style={{ color: '#f59e0b', fontStyle: 'italic' }}>Missing</span> : 
                    text,
            width: 150,
        },
        {
            title: 'Insurance',
            dataIndex: 'insuranceName',
            key: 'insuranceName',
            render: (text, record) => 
                record.missingInsurance ? 
                    <span style={{ color: '#f59e0b', fontStyle: 'italic' }}>Missing</span> : 
                    text,
            width: 150,
        },
        {
            title: 'Actions',
            key: 'action',
            width: 120,
            fixed: 'right',
            render: (_, row) => (
                <div style={{ display: 'flex', gap: '8px' }}>
                    <button
                        onClick={(e) => {
                            e.stopPropagation(); // Prevent row click
                            handlePerformExam(row);
                        }}
                        className="px-3 py-1.5 rounded-lg font-medium text-sm transition-colors"
                        style={{
                            backgroundColor: Globals.theme === 'dark' ? '#ec4899' : '#db2777',
                            color: 'white',
                            border: 'none',
                            cursor: 'pointer'
                        }}
                        onMouseEnter={(e) => {
                            e.currentTarget.style.backgroundColor = 
                                Globals.theme === 'dark' ? '#db2777' : '#be185d';
                        }}
                        onMouseLeave={(e) => {
                            e.currentTarget.style.backgroundColor = 
                                Globals.theme === 'dark' ? '#ec4899' : '#db2777';
                        }}
                    >
                        Enter Info
                    </button>
                </div>
            )
        }
    ], [formatMRN, formatDate, handlePerformExam]);

    /**
     * Get count color based on theme
     */
    const getCountColor = () => {
        return Globals.theme === 'dark' ? '#ec4899' : '#db2777';
    };

    /**
     * Get count message
     */
    const getCountMessage = () => {
        return count === 1 ? 'incomplete exam' : 'incomplete exams';
    };

    // COLLAPSED STATE - Show only count
    if (isCollapsed && !isExpanded) {
        if (isLoadingCount) {
            return (
                <div className="flex items-center justify-center py-4">
                    <div 
                        className="animate-spin rounded-full h-8 w-8 border-b-2"
                        style={{ borderColor: Globals.theme === 'dark' ? '#a1a1aa' : '#71717a' }}
                    />
                </div>
            );
        }

        return (
            <div className="flex flex-col items-center justify-center py-4">
                <div 
                    className="text-5xl font-light mb-2"
                    style={{ color: getCountColor() }}
                >
                    {count.toLocaleString()}
                </div>
                <div 
                    className="text-sm"
                    style={{ color: Globals.theme === 'dark' ? '#a1a1aa' : '#71717a' }}
                >
                    {getCountMessage()}
                </div>
            </div>
        );
    }

    // Success - no incomplete exams
    if (!isLoading && count === 0) {
        return (
            <div className="flex flex-col items-center justify-center py-4">
                <CheckCircle 
                    className="h-16 w-16 mx-auto mb-4"
                    style={{ color: getCountColor() }}
                />
                <p 
                    style={{ color: getCountColor() }}
                >
                    No Incomplete Exams Found
                </p>
            </div>
        );
    }

    // EXPANDED STATE - Show table with server-side pagination
    return (
        <>
            <div className="space-y-4">
                {/* Ant Design Table with server-side pagination */}
                <Table
                    columns={columns}
                    dataSource={sortedResults}
                    rowKey={(record, index) => `${record.patientName}-${record.examDateTime}-${record.mrn}-${record.examType}-${index}`}
                    pagination={{
                        current: currentPage,
                        pageSize: pageSize,
                        total: totalCount,
                        onChange: (page, newPageSize) => {
                            setCurrentPage(page);
                            if (newPageSize !== pageSize) {
                                setPageSize(newPageSize);
                                setCurrentPage(1); // Reset to page 1 when page size changes
                            }
                        },
                        pageSizeOptions: ['25', '50', '100', '200'],
                        showSizeChanger: true,
                        showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} exams`,
                    }}
                    size="small"
                    scroll={{ y: 500 }}
                    virtual
                    loading={{
                        spinning: (isLoading && !isExpanded) || isFetching
                    }}
                />
            </div>

            {/* Duplicate Exam Warning Modal */}
            <Modal
                visible={showDuplicateModal}
                title="Possible Duplicate Exams Detected"
                onCancel={handleDuplicateIgnore}
                footer={[
                    <button
                        key="review"
                        onClick={handleDuplicateReview}
                        className="px-3 py-1.5 rounded-lg font-medium text-sm"
                        style={{ backgroundColor: Globals.theme === 'dark' ? '#ec4899' : '#db2777', color: 'white', border: 'none', cursor: 'pointer', marginRight: 8 }}
                    >
                        Review
                    </button>,
                    <button
                        key="ignore"
                        onClick={handleDuplicateIgnore}
                        className="px-3 py-1.5 rounded-lg font-medium text-sm"
                        style={{ backgroundColor: 'transparent', border: `1px solid ${Globals.theme === 'dark' ? '#71717a' : '#d1d5db'}`, cursor: 'pointer', borderRadius: '0.5rem' }}
                    >
                        Ignore
                    </button>
                ]}
            >
                <p>This patient may have duplicate exams dated today. Please review the exam list and remove any duplicates where the accession number does not match the actual study performed.</p>
            </Modal>

            {/* Patient Exam Pane */}
            {showAddExamPane && selectedPatient && (
                <PatientExamPane
                    patientAge={calculateAge(selectedPatient?.dob)}
                    patientInactive={false}
                    patient={selectedPatient}
                    showPane={showAddExamPane}
                    examInfo={selectedPatientExam}
                    examHistory={examResults}
                    facility={facility}
                    updateExamInfo={updateExamInfo}
                    from='incomplete-exams'
                    hidePane={() => setShowAddExamPane(false)}
                    sharedFollowUpIDs={sharedFollowUpIDs}
                    getNewExamInfo={getNewExamInfo}
                />
            )}

        </>
    );
}

export default withRouter(IncompleteExamsWidget);
